import type { FinanceScope } from '../api/financeTypes'

/**
 * Admin-mode rules, kept pure so they are unit-tested:
 *  - the Felhasználó | Admin switch is visible iff the scope says the viewer
 *    is the global admin OR administers at least one project;
 *  - the choice is stored per viewer in localStorage (always try/catch — it
 *    can throw or come back empty), default user mode;
 *  - the `project_id` sent to the API follows the owner decision 2026-10-09
 *    (b): the global admin may omit it ("Minden projekt"); a project admin
 *    ALWAYS sends one.
 */

export type ViewMode = 'user' | 'admin'
export const ALL_PROJECTS = 'all'
const MODE_KEY = 'fs_view_mode'
const PROJECT_KEY = 'fs_admin_project'

export function switchVisible(scope: FinanceScope | null | undefined): boolean {
  return Boolean(scope && (scope.is_global_admin === true || (scope.projects?.length ?? 0) > 0))
}

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    /* private window / blocked storage: the choice just is not remembered */
  }
}

export function getStoredMode(): ViewMode {
  return read(MODE_KEY) === 'admin' ? 'admin' : 'user'
}

export function storeMode(mode: ViewMode): void {
  write(MODE_KEY, mode)
}

export function getStoredProject(): string | null {
  return read(PROJECT_KEY)
}

export function storeProject(projectId: string): void {
  write(PROJECT_KEY, projectId)
}

/** The project selector's options. The global admin additionally gets
 * "Minden projekt"; a project admin only sees his own projects. */
export function scopeOptions(scope: FinanceScope): { value: string; label: string }[] {
  const own = scope.projects.map((p) => ({ value: p.id, label: p.name || p.id }))
  return scope.is_global_admin ? [{ value: ALL_PROJECTS, label: 'Minden projekt' }, ...own] : own
}

/** The selection actually used: a stored/selected id that is no longer valid
 * (or "all" for a non-global admin) falls back to the first allowed option. */
export function resolveSelection(scope: FinanceScope, selected: string | null): string {
  const options = scopeOptions(scope)
  if (selected && options.some((o) => o.value === selected)) return selected
  return options[0]?.value ?? ''
}

/** `project_id` query value: undefined only for the global admin's "all". */
export function projectParam(scope: FinanceScope, selection: string): string | undefined {
  if (scope.is_global_admin && selection === ALL_PROJECTS) return undefined
  return selection || undefined
}

// --- periods (overview date range) -----------------------------------------

export type PeriodKey = '30d' | 'month' | 'quarter' | '12m'

export const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: '30d', label: '30 nap' },
  { key: 'month', label: 'Hónap' },
  { key: 'quarter', label: 'Negyedév' },
  { key: '12m', label: '12 hónap' },
]

const iso = (d: Date) => d.toISOString().slice(0, 10)

/** `from`/`to` (inclusive, UTC dates) for a period key. */
export function periodRange(key: PeriodKey, now: Date = new Date()): { from: string; to: string } {
  const y = now.getUTCFullYear()
  const m = now.getUTCMonth()
  const to = iso(now)
  switch (key) {
    case '30d':
      return { from: iso(new Date(Date.UTC(y, m, now.getUTCDate() - 29))), to }
    case 'month':
      return { from: iso(new Date(Date.UTC(y, m, 1))), to }
    case 'quarter':
      return { from: iso(new Date(Date.UTC(y, Math.floor(m / 3) * 3, 1))), to }
    case '12m':
      return { from: iso(new Date(Date.UTC(y, m - 11, 1))), to }
  }
}
